import {clsx} from 'clsx';
import _, {isEmpty} from 'lodash';
import React, {
  ComponentProps,
  FC,
  memo,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Popup} from 'semantic-ui-react';

import {ID} from '../../util/id';
import {SliderKeyboardOperation} from '../../util/media';
import {shiftInputByValue} from '../../util/shiftInputByValue';
// Doesn't yet work in nested panels yet.
// import {BetterPopup} from '../BetterPopup';
import NumberInput from './NumberInput';

export interface OnRenderSliderFunction {
  (
    opts: Pick<
      ComponentProps<'input'>,
      | 'disabled'
      | 'min'
      | 'max'
      | 'step'
      | 'value'
      | 'list'
      | 'onInput'
      | 'onChange'
      | 'onPointerDown'
      | 'onPointerUp'
      | 'onPointerCancel'
      | 'className'
    >
  ): ReactNode;
}

export interface SliderInputProps {
  min: number;
  max: number;
  step: number | 'any';
  value?: number;
  // if true, only input will be displayed, with slider appearing in a hover popup
  sliderInPopup?: boolean;
  className?: string;
  debounceTime?: number;
  trigger?: JSX.Element;
  hasInput?: boolean;
  minLabel?: string;
  maxLabel?: string;
  ticks?: number[];
  disabled?: boolean;
  strideLength?: number;
  keyboardBindings?: {
    [key: string]: SliderKeyboardOperation;
  };
  onChange(value: number): void;
  useStepperPlusMinus?: boolean;
  showTicks?: boolean;
  onRenderSlider?: OnRenderSliderFunction;
}

export const INPUT_SLIDER_CLASS = 'input__slider';

const SliderInput: FC<SliderInputProps> = memo(
  ({
    min,
    max,
    step,
    value,
    sliderInPopup,
    className,
    debounceTime,
    trigger,
    hasInput,
    minLabel,
    maxLabel,
    ticks,
    disabled,
    strideLength,
    keyboardBindings,
    onChange,
    onRenderSlider,
    useStepperPlusMinus = false,
    showTicks = true,
  }) => {
    const [sliderValue, setSliderValue] = useState(value ?? 0);
    const isDraggingRef = useRef(false);
    const sliderValueRef = useRef(sliderValue);

    const tickListID = useMemo(() => ID(10), []);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
    const onChangeDebounced = useCallback(
      _.debounce((v: number) => {
        onChange(v);
      }, debounceTime || 1),
      [onChange, debounceTime]
    );
    // On cleanup, commit the in-progress drag value or flush any pending debounce
    useLayoutEffect(() => {
      return () => {
        if (isDraggingRef.current) {
          onChangeDebounced.cancel();
          onChange(sliderValueRef.current);
        } else {
          onChangeDebounced.flush();
        }
      };
    }, [onChangeDebounced, onChange]);

    const updateVisual = useCallback(
      (newVal: number | undefined) => {
        if (newVal == null || !_.isFinite(newVal)) {
          return;
        }
        newVal = getClosestTick(
          newVal,
          sliderValueRef.current,
          min,
          max,
          ticks
        );
        setSliderValue(newVal);
        sliderValueRef.current = newVal;
      },
      [ticks, min, max]
    );

    const commitValue = useCallback(
      (newVal: number) => {
        onChangeDebounced.cancel();
        onChange(newVal);
      },
      [onChange, onChangeDebounced]
    );

    const update = useCallback(
      (newVal: number | undefined) => {
        if (newVal == null || !_.isFinite(newVal)) {
          return;
        }
        newVal = getClosestTick(newVal, sliderValue, min, max, ticks);
        setSliderValue(newVal);
        sliderValueRef.current = newVal;
        onChangeDebounced(newVal);
      },
      [ticks, min, max, sliderValue, onChangeDebounced]
    );

    const keyboardOperations = useMemo(() => {
      return {
        [SliderKeyboardOperation.INCREMENT]: (event: KeyboardEvent) => {
          const newValue = shiftInputByValue(
            ticks,
            strideLength,
            1,
            sliderValue,
            min,
            max
          );
          update(newValue);
        },
        [SliderKeyboardOperation.DECREMENT]: (event: KeyboardEvent) => {
          const newValue = shiftInputByValue(
            ticks,
            strideLength,
            -1,
            sliderValue,
            min,
            max
          );
          update(newValue);
        },
      };
    }, [sliderValue, update, ticks, strideLength, min, max]);

    const isFormField = useCallback((node?: Element | null | undefined) => {
      if (!node) {
        return false;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        return [
          'input',
          'textarea',
          'select',
          'button',
          'datalist',
          'output',
        ].includes(tagName);
      }

      return false;
    }, []);

    const stepKeyboardListener = useCallback(
      (event: KeyboardEvent) => {
        if (isEmpty(keyboardBindings) || isFormField(document.activeElement)) {
          return;
        }

        // Build the key combination string based on modifiers
        const modifiers = [];
        if (event.metaKey) modifiers.push('Meta');
        if (event.ctrlKey) modifiers.push('Control');

        const keyCombo = [...modifiers, event.key].join('+');
        const operation = (keyboardBindings ?? {})[keyCombo];

        if (operation && operation in keyboardOperations) {
          event.preventDefault();
          event.stopPropagation();
          const handler = keyboardOperations[operation];
          return handler(event);
        }
      },
      [keyboardOperations, keyboardBindings, isFormField]
    );

    useEffect(() => {
      if (!isEmpty(keyboardBindings)) {
        document.addEventListener('keydown', stepKeyboardListener, true);
      }
      return () => {
        document.removeEventListener('keydown', stepKeyboardListener, true);
      };
    }, [keyboardBindings, stepKeyboardListener]);

    useEffect(() => {
      if (value != null && !isDraggingRef.current) {
        setSliderValue(value);
        sliderValueRef.current = value;
      }
    }, [value]);

    const tickDatalist = useMemo(
      () =>
        ticks && (
          <datalist id={tickListID}>
            {ticks.map((t, i) => (
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              <option key={i} value={t}></option>
            ))}
          </datalist>
        ),
      [tickListID, ticks]
    );

    const renderSlider = () => {
      let slider: ReactNode;
      const inputProps: ComponentProps<'input'> = {
        // Other code relies on this class to detect if the event
        // comes from a slider, do not remove.
        disabled: disabled ?? false,
        min,
        max,
        step,
        value: sliderValue,
        list: showTicks ? tickListID : undefined,
        onInput: (e: SyntheticEvent<HTMLInputElement>) => {
          const newVal = parseFloat(e.currentTarget.value);
          if (isDraggingRef.current) {
            updateVisual(newVal);
          } else {
            update(newVal);
          }
        },
        onChange: (e: SyntheticEvent<HTMLInputElement>) => {
          // suppress warning about onChange missing
        },
        onPointerDown: (e: React.PointerEvent<HTMLInputElement>) => {
          isDraggingRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
        },
        onPointerUp: () => {
          isDraggingRef.current = false;
          commitValue(sliderValueRef.current);
        },
        onPointerCancel: () => {
          isDraggingRef.current = false;
          commitValue(sliderValueRef.current);
        },
      };
      if (onRenderSlider) {
        slider = onRenderSlider(inputProps);
      } else {
        // Default slider implementation
        slider = <input type="range" {...inputProps} />;
      }
      return (
        <div style={{display: 'flex', flexGrow: 1, alignItems: 'center'}}>
          {minLabel && <label className="min">{minLabel}</label>}
          {slider}
          {maxLabel && <label className="max">&nbsp;{maxLabel}</label>}
          {showTicks && tickDatalist}
        </div>
      );
    };

    const renderInput = () => {
      return (
        <NumberInput
          stepper
          strideLength={strideLength}
          disabled={disabled ?? false}
          min={min}
          max={max}
          value={sliderValue}
          ticks={ticks}
          onChange={update}
          useStepperPlusMinus={useStepperPlusMinus}
        />
      );
    };

    return (
      <div className={clsx('slider-input', className)}>
        {sliderInPopup ? (
          <Popup
            inverted
            size="mini"
            className={INPUT_SLIDER_CLASS}
            hoverable
            position="top center"
            // on="click"
            trigger={<span>{trigger ?? renderInput()}</span>}
            content={<div className="slider-input">{renderSlider()}</div>}
          />
        ) : (
          <>
            {renderSlider()}
            {trigger ?? (hasInput && renderInput())}
          </>
        )}
      </div>
    );
  }
);

export default SliderInput;

export function getClosestTick(
  val: number,
  prev: number,
  min: number,
  max: number,
  ticks?: number[]
): number {
  // if min/max not in ticks, allow coercion to nearest value
  if (val > max) {
    return max;
  }
  if (val < min) {
    return min;
  }
  if (ticks === null || ticks === undefined) {
    return val;
  }

  let closest = val;
  const increasing = val > prev;
  let minDiff = Number.MAX_VALUE;

  // Binary search for the closest tick
  let left = 0;
  let right = ticks.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const tick = ticks[mid];
    const diff = Math.abs(tick - val);

    // Only update closest if the tick is in the right direction
    if (
      diff < minDiff &&
      ((increasing && tick >= val) || (!increasing && tick <= val))
    ) {
      closest = tick;
      if (closest === val) {
        break;
      }
      minDiff = diff;
    }

    if (tick < val) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return closest;
}
